import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Button } from "@components/ui/Button"
import { Chip } from "@components/ui/Chip"
import { DropdownMenu } from "@components/ui/DropdownMenu"
import { IconCross } from "@components/ui/Icon"
import { Select } from "@components/ui/Select"
import { FerveCartItem } from "@features/ferve/ferve-cart"
import { useFerve } from "@features/ferve/FerveProvider"
import { useResolvedCartSession } from "@features/ferve/useResolvedCartSession"
import { EventType } from "@generated-types"
import { formatDollars } from "@utils/money-format"
import { tv } from "@utils/tw"
import { useAppData } from "eddev/hooks"
import { useRouter } from "eddev/routing"
import { AnimatePresence, motion, MotionProps } from "motion/react"
import { ReactNode } from "react"

type Props = {
  /**
   * 1 or more cart items
   * When multiple items are present, it means they're multiple ticket types (eg. adult, concession) for the same film.
   */
  items: FerveCartItem[]
}

export function CartItem(props: Props) {
  const item = props.items[0]

  if (item.isCartDonation) return null

  console.log("Item", item)

  if (item.isFlexipass || item.isGiftCard) {
    return <PassItem items={props.items} />
  } else if (item.isDonation) {
    return <DonationItem items={props.items} />
  } else if (item.isMerchandise) {
    return <MerchItem items={props.items} />
  }
  return <EventItem items={props.items} />
}

type EventItemProps = {
  items: FerveCartItem[]
}

function EventItem(props: EventItemProps) {
  const cart = useFerve().cart
  const router = useRouter()

  const item = props.items[0]

  // We want it in the format: "2025-06-08T15:45:00"
  const { film, session } = useResolvedCartSession(item)

  const itemDate = item.dateText
  const itemVenue = item.venueText

  return (
    <motion.div className="rounded-md bg-bg-low" layoutRoot>
      <div className="flex gap-4 type-ui-m p-3">
        {/* Image */}
        {film?.media?.thumb && (
          <div className="aspect-[4/3] -m-1 w-[20%] md:w-[25%] bg-bg-mid rounded-sm flex-none overflow-hidden relative">
            <ResponsiveImage {...film.media.thumb} max />
          </div>
        )}

        {/* Details */}
        <div className="flex flex-col gap-2 flex-1">
          <div>{film?.title ?? item.title}</div>
          <div className="flex flex-col gap-2 md:gap-1 items-start">
            {itemDate && (
              <Chip shape="rounded" variant="high">
                {itemDate}
              </Chip>
            )}
            {itemVenue && (
              <Chip shape="rounded" variant="high">
                {itemVenue}
              </Chip>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="w-min flex-none">
          <DropdownMenu
            trigger={<Button shape="icon" icon="ellipsis" variant="ghost" />}
            align="end"
            items={[
              {
                type: "item",
                icon: "externalLink",
                label: film?.eventInfo?.eventType === EventType.Film ? "View Film Page" : "View Session",
                disabled: !film?.uri,
                onSelect: () => {
                  router.navigate(film?.uri!)
                },
              },
              {
                type: "separator",
              },
              {
                type: "item",
                icon: "cross",
                label: "Remove",
                onSelect: () => {
                  cart.remove(
                    props.items.map((item) => item.hash),
                    true,
                    "remove-event",
                  )
                },
              },
            ]}
          />
        </div>
      </div>
      <AnimatePresence mode="popLayout" initial={false} presenceAffectsLayout>
        {props.items.map((item, i) => {
          return (
            <motion.div key={item.hash} {...rowMotion} layoutId={item.hash}>
              <SubRow
                price={item.price * item.quantity}
                title={<span dangerouslySetInnerHTML={{ __html: item.priceTypeLabel ?? "" }} />}
                subtitle={item.seats?.join(", ")}
                quantity={item.quantity}
                quantityOptions={item.quantityOptions}
                onQuantityChange={(quantity) => {
                  cart.updateQuantity(item.hash, quantity)
                }}
                onRemove={() => {
                  cart.remove(item.hash, true, "remove-ticket")
                }}
              />
              {item.children.map((child) => {
                let title = child.title || child.priceTypeLabel
                let subtitle = child.dateText + (child.venueText ? ` at ${child.venueText}` : "")
                if (child.priceTypeLabel === item.priceTypeLabel && child.dateText) {
                  title = child.dateText
                  subtitle = child.venueText ?? ""
                }
                return (
                  <SubRow
                    key={child.hash}
                    price={child.price * child.quantity}
                    title={title}
                    subtitle={subtitle}
                    quantity={child.quantity}
                  />
                )
              })}
            </motion.div>
          )
        })}
      </AnimatePresence>
    </motion.div>
  )
}

function MerchItem(props: EventItemProps) {
  const cart = useFerve().cart
  const router = useRouter()

  const item = props.items[0]

  const merchItem = useAppData((data) => data.merchandiseItems?.nodes)?.find(
    (node) => node.info?.ferveTitle === item.title,
  )

  return (
    <motion.div className="rounded-md bg-bg-low" layoutRoot>
      <div className="flex gap-4 type-ui-m p-3">
        {/* Image */}
        {merchItem?.thumbnail && (
          <div className="aspect-[4/3] -m-1 w-[20%] md:w-[25%] bg-bg-mid rounded-sm flex-none overflow-hidden relative">
            <img src={merchItem?.thumbnail} className="object-cover w-full h-full" />
          </div>
        )}

        {/* Details */}
        <div className="flex flex-col gap-2 flex-1">
          <div>{item.title}</div>
        </div>

        {/* Actions */}
        <div className="w-min flex-none">
          <DropdownMenu
            trigger={<Button shape="icon" icon="ellipsis" variant="ghost" />}
            align="end"
            items={[
              {
                type: "item",
                icon: "cross",
                label: "Remove",
                onSelect: () => {
                  cart.remove(
                    props.items.map((item) => item.hash),
                    true,
                    "remove-event",
                  )
                },
              },
            ]}
          />
        </div>
      </div>
      <AnimatePresence mode="popLayout" initial={false} presenceAffectsLayout>
        {props.items.map((item, i) => {
          return (
            <motion.div key={item.hash} {...rowMotion} layoutId={item.hash}>
              <SubRow
                price={item.price * item.quantity}
                title={item.priceTypeLabel}
                subtitle={item.seats?.join(", ")}
                quantity={item.quantity}
                quantityOptions={item.quantityOptions}
                onQuantityChange={(quantity) => {
                  cart.updateQuantity(item.hash, quantity)
                }}
                onRemove={() => {
                  cart.remove(item.hash, true, "remove-ticket")
                }}
              />
            </motion.div>
          )
        })}
      </AnimatePresence>
    </motion.div>
  )
}

function DonationItem(props: EventItemProps) {
  const cart = useFerve().cart

  return (
    <motion.div className="rounded-md bg-bg-low" layoutRoot>
      <div className="flex gap-4 type-ui-m p-3">
        {/* Details */}
        <div className="flex flex-col gap-2 flex-1">
          <div dangerouslySetInnerHTML={{ __html: props.items[0].title ?? "" }}></div>
          <div className="type-ui-s text-fg-secondary">
            Thank you for taking a leading role in Sydney Film Festival's future by donating today
          </div>
        </div>

        {/* Actions */}
        <div className="w-min flex-none">
          <DropdownMenu
            trigger={<Button shape="icon" icon="ellipsis" variant="ghost" />}
            align="end"
            items={[
              {
                type: "item",
                icon: "cross",
                label: "Remove",
                onSelect: () => {
                  cart.remove(
                    props.items.map((item) => item.hash),
                    true,
                    "remove-donation",
                  )
                },
              },
            ]}
          />
        </div>
      </div>
      <AnimatePresence mode="popLayout" initial={false} presenceAffectsLayout>
        {props.items.map((item, i) => {
          return (
            <motion.div key={item.hash} {...rowMotion} layoutId={item.hash}>
              <SubRow
                price={item.price * item.quantity}
                title={item.priceTypeLabel || "Donation"}
                subtitle={"Tax-deductible"}
                quantity={undefined}
                quantityOptions={[]}
                onQuantityChange={(quantity) => {
                  // console.log("Quantity change", quantity)
                  cart.updateQuantity(item.hash, quantity)
                }}
                onRemove={() => {
                  cart.remove(item.hash, true, "remove-donation-subitem")
                }}
              />
            </motion.div>
          )
        })}
      </AnimatePresence>
    </motion.div>
  )
}

function PassItem(props: EventItemProps) {
  const cart = useFerve().cart
  const router = useRouter()

  return (
    <div>
      <AnimatePresence mode="popLayout" initial={false} presenceAffectsLayout>
        {props.items.map((item, i) => {
          return (
            <motion.div key={item.hash} {...rowMotion} layoutId={item.hash}>
              <SubRow
                price={item.price * item.quantity}
                title={<span className="type-ui-m" dangerouslySetInnerHTML={{ __html: item.title ?? "" }} />}
                subtitle={<span>{formatDollars(item.price)}</span>}
                quantity={item.quantity}
                quantityOptions={item.quantityOptions}
                onQuantityChange={(quantity) => {
                  // console.log("Quantity change", quantity)
                  cart.updateQuantity(item.hash, quantity)
                }}
                standalone={i === 0}
                onRemove={() => {
                  cart.remove(item.hash, true, "remove-pass")
                }}
              />
            </motion.div>
          )
        })}
      </AnimatePresence>
    </div>
  )
}

type SubRowProps = {
  standalone?: boolean
  title?: ReactNode
  subtitle?: ReactNode
  quantity?: number
  quantityOptions?: string[]
  onQuantityChange?: (quantity: number) => void
  price?: number
  onRemove?: () => void
}

function SubRow(props: SubRowProps) {
  const styles = subrowStyles({ standalone: props.standalone })
  return (
    <div className={styles.base()}>
      <div className={styles.info()}>
        {props.title && <div className="text-fg">{props.title}</div>}
        {props.subtitle && <div className="text-fg-secondary mt-[0.3em]">{props.subtitle}</div>}
      </div>
      <div className={styles.quantity()}>
        {props.quantityOptions?.length! > 1 ? (
          <Select
            value={String(props.quantity)}
            items={props.quantityOptions!.map((v) => ({
              type: "item",
              label: String(v),
              value: String(v),
              icon: undefined,
            }))}
            variant="outline"
            onValueChange={(v) => {
              const value = v ? parseInt(v) : undefined
              if (value) {
                props.onQuantityChange?.(value)
              } else {
                props.onRemove?.()
              }
            }}
          />
        ) : (
          <span className="type-ui-m">{props.quantity}</span>
        )}
      </div>
      <div className={styles.price()}>{formatDollars(props.price!)}</div>
      <div className={styles.remove()}>
        <button className="inline-flex size-[16px] items-center justify-center" onClick={props.onRemove}>
          <IconCross className="block" />
        </button>
      </div>
    </div>
  )
}

const subrowStyles = tv({
  base: "flex px-4 md:px-3 py-3 items-center border-t rounded-b-md border-bg-mid gap-2 bg-bg-low",
  slots: {
    info: "flex flex-col flex-1 type-ui-s",
    quantity: "flex-none type-ui-s",
    price: "min-w-[5em] flex-auto flex-grow-0 type-ui-m text-right",
    remove: "min-w-[16px] flex-none flex",
  },
  variants: {
    standalone: {
      true: {
        base: "border-t-0 rounded-t-md",
      },
    },
  },
})

const rowMotion: MotionProps = {
  initial: { opacity: 0, scale: 0.7 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.7 },
  layout: "position",
}
